import { useCallback } from 'react'
import { Role } from 'client/enums/Role'
import { LevelRole } from 'client/constants/levelRole'
import { RoleAccessStatus } from 'client/enums/RoleAccessStatus'
import { useDispatch } from 'react-redux'
import { showSubscribeDrawer, showUpgradeDrawer } from 'client/actions/uiActions'
import useRoles from './useRoles'

const useRoleAccess = () => {
  const { isIdentified, isSubscriber, isQRHost } = useRoles()
  const dispatch = useDispatch()

  const hasRequiredRole = useCallback((requiresRole: Role) => {
    return requiresRole === Role.anonymous
      || (requiresRole === Role.identified && isIdentified)
      || (requiresRole === Role.subscriber && isSubscriber)
      || (requiresRole === Role.qrHost && isQRHost)
  }, [isIdentified, isSubscriber, isQRHost])

  const getRoleAccessStatus = useCallback((requiredRole: Role) => {
    if (hasRequiredRole(requiredRole)) {
      return RoleAccessStatus.canPlay
    }

    if (requiredRole === Role.identified && !isIdentified) {
      return RoleAccessStatus.shouldIdentify
    }

    if (requiredRole === Role.subscriber && !isSubscriber) {
      return RoleAccessStatus.shouldSubscribe
    }

    if (requiredRole === Role.qrHost && !isQRHost) {
      return RoleAccessStatus.shouldBeQRHost
    }

    return RoleAccessStatus.unknown
  }, [hasRequiredRole, isIdentified, isSubscriber, isQRHost])

  const canPlaySomeLevel = useCallback((levelRoles: LevelRole[] = []) => {
    return levelRoles.some(({ requiresRole }) => hasRequiredRole(requiresRole))
  }, [hasRequiredRole])

  // returns the minimal required role for the levelRoles
  const getMinimalRequiredRole = useCallback((levelRoles: LevelRole[] = []): Role => {
    return [Role.anonymous, Role.identified, Role.subscriber, Role.qrHost]
      .find((role) => levelRoles.some(({ requiresRole }) => requiresRole === role))
  }, [])

  // show the upgrade or subscribe drawer based on the minimal required role
  const showUpgradeOrSubscribeDrawer = useCallback((levelRoles: LevelRole[] = []) => {
    const minimalRequiredRole = getMinimalRequiredRole(levelRoles)
    const roleAccessStatus = getRoleAccessStatus(minimalRequiredRole)

    if (roleAccessStatus === RoleAccessStatus.shouldIdentify) {
      dispatch(showUpgradeDrawer())
    }
    if (roleAccessStatus === RoleAccessStatus.shouldSubscribe) {
      dispatch(showSubscribeDrawer())
    }
  }, [getMinimalRequiredRole, getRoleAccessStatus, dispatch])

  return {
    canPlaySomeLevel,
    hasRequiredRole,
    getRoleAccessStatus,
    showUpgradeOrSubscribeDrawer,
  }
}

export default useRoleAccess
